import {skipToken} from '@reduxjs/toolkit/query';
import {Heading, HStack, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

import {CompareTrend} from '../../../../../components/CompareTrend/CompareTrend';
import i18n from '../../../../../i18n/i18n';
import {useGetPriceReportCarDetailQuery} from '../../../../../store/priceReportApi';
import {PriceReportType} from '../../../../../types/PriceReportType';

interface AdvertisedPriceChartProps extends TestIdProps {
  priceReport: PriceReportType;
}

export function AdvertisedPriceChart(props: AdvertisedPriceChartProps) {
  const formatCurrency = useFormatCurrency();
  const {adId} = props.priceReport.vehicle;
  const {data: advertisedVehicle} = useGetPriceReportCarDetailQuery(
    isNotNil(adId)
      ? {
          reportId: props.priceReport.id,
          vehicleId: adId,
        }
      : skipToken
  );

  if (isNil(advertisedVehicle)) {
    return null;
  }

  const advertisedPrice = advertisedVehicle.price_with_vat;
  const priceDifference = isNotNil(advertisedPrice)
    ? advertisedPrice - props.priceReport.sellingPrice.recommended.value
    : null;
  const currency = props.priceReport.sellingPrice.recommended.currency;

  return (
    <VStack align="center" spacing={2}>
      <Text
        size="small"
        color="secondary"
        align="center"
        data-testid={suffixTestId('heading', props)}
      >
        {i18n.t('entity.advertisedPriceChart.labels.heading')}
      </Text>
      <HStack align="center" spacing={2}>
        <Heading size={4} data-testid={suffixTestId('advertisedPrice', props)}>
          {formatCurrency(advertisedPrice ?? 0, currency)}
        </Heading>
        <CompareTrend
          value={priceDifference ?? 0}
          formatter={(value) => formatCurrency(value, currency)}
          data-testid={suffixTestId('priceDifference', props)}
        />
      </HStack>
    </VStack>
  );
}
